import PropTypes from 'prop-types';
import { useAuth } from '../../../../providers/AuthProvider';
import AppButton from '../../../../components/AppButton';
import { getAvailabilityStatus } from './availabilityStatus';

/**
 * Dashboard banner shown when the talent's availability has expired or expires soon.
 * Uses the same rules as AvailabilityCard (see availabilityStatus.js).
 */

const TONES = {
    expired: {
        container: 'border-red-200 border-l-danger bg-red-50',
        accent: 'bg-danger',
    },
    expiring: {
        container: 'border-amber-200 border-l-amber-500 bg-amber-50',
        accent: 'bg-amber-500',
    },
};

const expiringBadge = (days) => {
    if (days === 0) return 'Expires today';
    return `Expires in ${days} day${days === 1 ? '' : 's'}`;
};

const AvailabilityBanner = ({ onResetAvailability, className = '' }) => {
    const { auth } = useAuth();
    if (!auth) return null;

    const { expirationDate, isExpired, isExpiring, daysUntilExpiry } = getAvailabilityStatus(auth);
    if (!isExpired && !isExpiring) return null;

    const tone = TONES[isExpired ? 'expired' : 'expiring'];

    const badge = isExpired ? 'Expired' : expiringBadge(daysUntilExpiry);
    const title = isExpired
        ? (expirationDate ? `Your availability expired ${expirationDate.format('MMM D')}` : 'Your availability hasn’t been confirmed')
        : `Your availability expires ${expirationDate.format('MMM D')}`;
    const message = isExpired
        ? 'Clients don’t see you as available until you reset it'
        : 'Reset it to keep showing as available to clients';

    return (
        <div
            role={isExpired ? 'alert' : 'status'}
            className={`flex flex-col gap-3 rounded-xl border border-l-4 border-solid px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-5 ${tone.container} ${className}`}
        >
            <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${tone.accent}`} aria-hidden="true">
                    !
                </span>

                <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3">
                    <span className={`w-fit whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white ${tone.accent}`}>
                        {badge}
                    </span>
                    <span className="text-[15px] font-semibold text-gray-900">{title}</span>
                    <span className="text-sm text-gray-600">{message}</span>
                </div>
            </div>

            {onResetAvailability && (
                <AppButton onClick={() => onResetAvailability()} className="w-full text-xs shrink-0 sm:w-auto">
                    Reset for 30 days
                </AppButton>
            )}
        </div>
    );
};

AvailabilityBanner.propTypes = {
    onResetAvailability: PropTypes.func,
    className: PropTypes.string,
};

export default AvailabilityBanner;
